import { collabStateManager } from '@databases/collab-state-manager';
import { isAnyNativeEmbed } from '@databases/modes';
import { storage as createWithTemplateStorage } from '@databases/templates/storage';
import { useEffect, useState } from 'react';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getFrameApi } from '@canvas/frame-api/host-api/host-frame-context';
import { PaywallFeature, useIsPaywallUnlocked } from '@canvas/paywall';

export const useShouldShowDatabaseBlankObjectExperience = ({
  contentId,
  isEmptyDatabase,
  isDbAiFirstCreationEnabled,
  isDatabaseLoaded,
  isInAIFCStagingAreaView
}: {
  contentId: string;
  isEmptyDatabase: boolean;
  isDbAiFirstCreationEnabled: boolean;
  isDatabaseLoaded: boolean;
  isInAIFCStagingAreaView: boolean;
}) => {
  const isAiTeammatePaywallUnlocked = useIsPaywallUnlocked(PaywallFeature.AI_TEAMMATE);
  const collabManager = collabStateManager.getCollabManager(contentId);
  const wasCreatedThisSession = Boolean(collabManager?.wasCreatedThisSession);
  const frameApiHref = getFrameApi().href;
  const frameApiHrefUrl = new URL(frameApiHref);
  const hasAiCreateFlowQueryParam =
    frameApiHrefUrl.searchParams.get('flow') === 'aiCreate';

  const [shouldShowBlankObjectExperience, setShouldShowBlankObjectExperience] =
    useState<boolean>(false);

  useEffect(() => {
    const wasCreatedFromTemplate =
      createWithTemplateStorage.exists(contentId) &&
      isFeatureFlagEnabled('applyDatabaseTemplateFromQuickInsert');
    const isNativeEmbed = isAnyNativeEmbed();

    if (
      !isDatabaseLoaded ||
      !isEmptyDatabase ||
      !isDbAiFirstCreationEnabled ||
      !isAiTeammatePaywallUnlocked ||
      !wasCreatedThisSession ||
      hasAiCreateFlowQueryParam ||
      isInAIFCStagingAreaView ||
      isNativeEmbed ||
      wasCreatedFromTemplate
    ) {
      setShouldShowBlankObjectExperience(false);
      return;
    }

    const shouldShowExperience = getExperimentValue(
      'cwrCanvasBlankObjectExperiment',
      'isEnabled',
      {
        fireExperimentExposure: true
      }
    );

    setShouldShowBlankObjectExperience(shouldShowExperience);
  }, [
    contentId,
    hasAiCreateFlowQueryParam,
    isAiTeammatePaywallUnlocked,
    isEmptyDatabase,
    isDatabaseLoaded,
    isDbAiFirstCreationEnabled,
    isInAIFCStagingAreaView,
    wasCreatedThisSession
  ]);

  return shouldShowBlankObjectExperience;
};
